import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,  // cookies httpOnly de autenticación
});

// ── Cliente dedicado al panel /admin ─────────────────────────────────────
// SEGURIDAD (decisión consciente): almacenamos el Bearer token del super-admin
// en localStorage y NO en cookie HttpOnly. Razón:
//   1. El backend vive en un subdominio distinto del frontend (recobro.site vs
//      colecta-auto.preview.emergentagent.com). Cloudflare/Emergent ingress
//      reescribe Access-Control-Allow-Origin a '*' cuando hay credentials,
//      lo que invalida cualquier cookie cross-site.
//   2. El preview de Emergent corre embebido en un iframe: los navegadores
//      modernos bloquean cookies de terceros dentro del iframe, dejando el
//      panel /admin inaccesible desde la vista de dev.
// Mitigaciones aplicadas:
//   - El token vive sólo mientras el super-admin lo usa; hay logout explícito.
//   - Los endpoints /admin verifican el Bearer en cada request (no hay sesión implícita).
//   - Cualquier XSS en /admin es de todos modos catastrófico (control total del panel),
//     así que localStorage vs httpOnly cookie no cambia el radio de daño.
// Si en el futuro backend y frontend viven en el MISMO dominio (recobro.site),
// migrar a HttpOnly cookie + CSRF token doble-envío.
const ADMIN_TOKEN_KEY = "recobro_admin_token";
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY) || "";
export const setAdminToken = (t) => {
  if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
};

const adminAxios = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});
adminAxios.interceptors.request.use((cfg) => {
  const t = getAdminToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const authApi = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (email, password, remember = false) =>
    api.post("/auth/login", { email, password, remember }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateMe: (patch) => api.patch("/auth/me", patch).then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  usage: () => api.get("/auth/me/usage").then((r) => r.data),
  refresh: () => api.post("/auth/refresh").then((r) => r.data),
};

export const adminApi = {
  login: async (email, password) => {
    const r = await adminAxios.post("/admin/login", { email, password });
    if (r.data?.token) setAdminToken(r.data.token);
    return r.data;
  },
  logout: async () => {
    setAdminToken("");
    try { await adminAxios.post("/admin/logout"); } catch { /* idempotente */ }
    return { ok: true };
  },
  me: () => adminAxios.get("/admin/me").then((r) => r.data),
  listUsers: () => adminAxios.get("/admin/users").then((r) => r.data),
  updateUser: (id, patch) => adminAxios.patch(`/admin/users/${id}`, patch).then((r) => r.data),
  deleteUser: (id) => adminAxios.delete(`/admin/users/${id}`).then((r) => r.data),
  notifications: () => adminAxios.get("/admin/notifications").then((r) => r.data),
  markNotificationRead: (id) => adminAxios.post(`/admin/notifications/${id}/read`).then((r) => r.data),
};

export const debtorApi = {
  list: () => api.get("/debtors").then((r) => r.data),
  get: (id) => api.get(`/debtors/${id}`).then((r) => r.data),
  create: (data) => api.post("/debtors", data).then((r) => r.data),
  update: (id, data) => api.patch(`/debtors/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/debtors/${id}`).then((r) => r.data),
  seedDemo: () => api.post("/debtors/seed-demo").then((r) => r.data),
  templateUrl: `${API}/debtors/template.xlsx`,
  uploadCsv: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/debtors/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  classify: (id) => api.post(`/debtors/${id}/classify`).then((r) => r.data),
  generateMessage: (id, channel = "email") =>
    api.post(`/debtors/${id}/generate-message?channel=${channel}`).then((r) => r.data),
  analyzeReply: (id, reply) =>
    api.post(`/debtors/${id}/analyze-reply`, { reply }).then((r) => r.data),
  markStatus: (id, status) =>
    api.post(`/debtors/${id}/mark-status`, { status }).then((r) => r.data),
  cancelCuota: (id, numero) =>
    api.post(`/debtors/${id}/cuotas/${numero}/cancel`).then((r) => r.data),
  registerAbono: (id, payload) =>
    api.post(`/debtors/${id}/abonos`, payload).then((r) => r.data),
  sweepPromises: () => api.post("/debtors/sweep-promises").then((r) => r.data),
  listPromises: () => api.get("/promises").then((r) => r.data),
  agenda: () => api.get("/agenda").then((r) => r.data),
  runPreventiveReminder: () => api.post("/agenda/preventive-reminder").then((r) => r.data),
  clearAll: (password) =>
    api.post("/debtors/clear-all", { password }).then((r) => r.data),
};

export const bankApi = {
  get: () => api.get("/bank-config").then((r) => r.data),
  save: (data) => api.post("/bank-config", data).then((r) => r.data),
};

export const receiptApi = {
  list: () => api.get("/receipts").then((r) => r.data),
  validate: (file, debtorId) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = debtorId ? `?debtor_id=${debtorId}` : "";
    return api
      .post(`/receipts/validate${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  imageUrl: (id) => `${API}/receipts/${id}/image`,
};

export const metricsApi = {
  get: () => api.get("/metrics").then((r) => r.data),
  balancePdfUrl: `${API}/metrics/balance-pdf`,
};

export const quotesApi = {
  list: () => api.get("/quotes").then((r) => r.data),
  get: (id) => api.get(`/quotes/${id}`).then((r) => r.data),
  create: (data) => api.post("/quotes", data).then((r) => r.data),
  remove: (id) => api.delete(`/quotes/${id}`).then((r) => r.data),
  pdfUrl: (id) => `${API}/quotes/${id}/pdf`,
};

export const configApi = {
  public: () => api.get("/config/public").then((r) => r.data),
};

export const educationApi = {
  list: () => api.get("/education").then((r) => r.data),
  adminList: () => api.get("/admin/education").then((r) => r.data),
  adminCreate: (data) => api.post("/admin/education", data).then((r) => r.data),
  adminUpdate: (id, data) => api.patch(`/admin/education/${id}`, data).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/education/${id}`).then((r) => r.data),
};

export const adminExtraApi = {
  revealPassword: (userId, superadmin_password) =>
    api.post(`/admin/users/${userId}/reveal-password`, { superadmin_password }).then((r) => r.data),
};
